import React, { useContext, useEffect, useState } from "react";
import { marked } from "marked";
import "./TypingEffect.scss";
import { getBlobUrl, openPdfInNewTab } from "../openPdfInTab";
import { EnvironmentConfig } from "@me8eon/config";
import LoaderComponent from "../../../loader-component/LoaderComponent";
import { parsedLinkArray } from "../parseLinks";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useJwtToken } from "@me8eon/react_authentication";
import { useThrowError } from "@me8eon/throw_error";
import { AiAssistContext } from "../../AiAssist.context";

interface ITypingEffectProps {
    text: string;
    speed: number;
    effecttEnd?: () => void;
    className: string;
}

type Link = {
    url: string;
    text: string | null;
};

const TypingEffect: React.FC<ITypingEffectProps> = ({
    text,
    speed,
    effecttEnd,
    className,
}) => {
    const { isChatHistory } = useContext(AiAssistContext);
    const [count, setCount] = useState<number>(0);
    const [linksArray, setLinksArray] = useState<Link[]>([]);
    const [textRendered, setTextRendered] = useState<boolean>(false);
    const [showPreview, setShowPreview] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const jwt = useJwtToken();
    const reportError = useThrowError();

    useEffect(() => {
        if (isChatHistory) {
            setCount(text.length);
            setTextRendered(true);
            effecttEnd && effecttEnd();
        } else {
            const interval = setInterval(() => {
                setCount((prevCount) => {
                    if (prevCount < text.length) {
                        if (!text[prevCount + 1]) {
                            setTextRendered(true);
                        }
                        return prevCount + 1;
                    } else {
                        clearInterval(interval);
                        effecttEnd && effecttEnd();
                        return prevCount;
                    }
                });
            }, speed);
            return () => clearInterval(interval);
        }
    }, [text, speed, isChatHistory]);

    useEffect(() => {
        setLinksArray(parsedLinkArray(text));
    }, [text]);

    const handleCitationUrl = async (url: string) => {
        const jwtToken = await jwt();
        if (!jwtToken) return reportError("s/w", "jwtToken is not available");
        if (window.innerWidth > 920 && isPdfLink(url)) {
            setIsLoading(true);
            const blobUrl = isGingoPdfApi(url)
                ? await getBlobUrl(url, jwtToken)
                : url;
            setIsLoading(false);
            if (showPreview === blobUrl) {
                setShowPreview("");
                return;
            }
            setShowPreview(blobUrl);
        } else if (isGingoPdfApi(url)) {
            setIsLoading(true);
            await openPdfInNewTab(url, jwtToken);
            setIsLoading(false);
        } else {
            window.open(url, "_blank");
        }
    };

    const isPdfLink = (url: string): boolean => {
        return false;
        // return (url.match("snowkbforme8eon") !== null ||
        //     url.match("genai") !== null ||
        //     url.match(".pdf") !== null);
    };

    const isGingoPdfApi = (url: string): boolean => {
        return (
            url.startsWith(EnvironmentConfig.genAiApiBaseUrl as string) &&
            url.split(/[?#]/)[0].endsWith(".pdf")
        );
    };

    return (
        <>
            <div
                className={`ai-response ${className} ${isChatHistory ? "chat-history" : ""}`}
                dangerouslySetInnerHTML={{
                    __html: marked.parseInline(text.slice(0, count)) as string,
                }}
            />
            <LoaderComponent isLoading={isLoading} />
            <div className={`${showPreview ? "iframe-with-links" : ""}`}>
                {textRendered && linksArray && linksArray.length > 0 && (
                    <div className="citation-container">
                        {linksArray.map((link: Link, index: number) => (
                            isPdfLink(link.text ?? link.url) && (
                                <EonUiButton
                                    size="small"
                                    rank="secondary"
                                    text={link.text ?? link.url}
                                    onClick={() => handleCitationUrl(link.url)}
                                    key={`button_${index + 1}`}
                                    className="citation-item"
                                />
                            )
                        ))}
                    </div>
                )}
                {showPreview && (
                    <iframe
                        className="pdf-preview"
                        title="internal-documents"
                        src={showPreview}
                    />
                )}
            </div>
        </>
    );
};

export default TypingEffect;
